import React, {FC, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useApolloClient} from "@apollo/client";
import {MailOutlined, SendOutlined} from "@ant-design/icons";
import {Alert, Button, Checkbox, Input, Modal, Select, Space, Table, Typography, App} from "antd";
import type {TableColumnsType} from "antd";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../../Styles/NotificationsPage.css";
import {
    buildNotificationHistoryVariables,
    formatNotificationTimestamp,
    notificationStatusTone,
    parsePositiveIntParam,
    sanitizeNotificationFailureMessage,
    stripHtmlForPreview
} from "./notificationHistoryPageHelpers";
import {buildUserFacingErrorLabels, getUserFacingErrorMessage} from "../../userFacingErrorUtils";
import {ADMIN_MAX_PAGE_SIZE} from "./adminPaging";
import {parseEventEmailTemplateBody, parseEventNotificationMessage, serializeEventNotificationMessage} from "./eventEmailTemplateBody";
import {AuthContext} from "../../Contexts/AuthContext";
import {dateSorter, sortText, textSorter} from "./tableSorters";
import {humanizeEnumValue, normalizeEnumKey} from "../../i18n/labelHelpers";
import {StatusTag} from "../../Components/ui/StatusTag";
import {PageHeader} from "../../Components/ui";
import {
    EventNotificationChannel,
    EventNotificationRecipientsDocument,
    EventNotificationRecipientsQuery,
    EventNotificationTemplatesQuery,
    EventParticipantNotificationHistoryQuery,
    useEventNotificationTemplatesQuery,
    useEventParticipantNotificationHistoryQuery,
    useRetryFailedEventRegistrationNotificationsMutation,
    useSaveEventRegistrationNotificationDraftMutation,
    useSendEventRegistrationNotificationMutation
} from "../../GraphQL/generated/graphql";

const PAGE_SIZE = 50;
type NotificationChannelValue = EventNotificationChannel.Email;
const richTextModules = {
    toolbar: [
        [{header: [1, 2, false]}],
        ["bold", "italic", "underline", "strike", "blockquote"],
        [
            {list: "ordered"},
            {list: "bullet"},
            {indent: "-1"},
            {indent: "+1"}
        ],
        ["link", "code"],
        ["clean"]
    ]
};
const richTextFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "code"
];

type NotificationHistoryLog = NonNullable<NonNullable<EventParticipantNotificationHistoryQuery["eventParticipantNotificationHistory"]>["items"]>[number];

type NotificationHistoryGroup = {
    key: string;
    id: string;
    representative: NotificationHistoryLog;
    logs: NotificationHistoryLog[];
};

type NotificationTemplate = NonNullable<NonNullable<EventNotificationTemplatesQuery["eventEmailTemplates"]>["items"]>[number];

type NotificationRecipient = NonNullable<EventNotificationRecipientsQuery["eventNotificationRecipients"]>[number];

type DraftLoadState = {
    draftLog: NotificationHistoryLog;
    channels: NotificationChannelValue[];
    replyToEmails: string[];
    recipientIds: number[];
    missingRecipientLabels: string[];
};

const notificationChannelOptions: Array<{label: string; value: NotificationChannelValue}> = [
    {label: "Email", value: EventNotificationChannel.Email}
];

const dedupeText = (values: Array<string | null | undefined>) => {
    const seen = new Set<string>();
    const parts: string[] = [];

    values
        .map(value => value?.trim())
        .filter((value): value is string => !!value)
        .forEach(value => {
            if (seen.has(value)) {
                return;
            }

            seen.add(value);
            parts.push(value);
        });

    return parts;
};

const normalizeEmailList = (values: Array<string | null | undefined>) => {
    const seen = new Set<string>();
    const emails: string[] = [];

    values
        .map(value => value?.trim() ?? "")
        .filter(value => !!value)
        .forEach(value => {
            const key = value.toLocaleLowerCase();
            if (seen.has(key)) {
                return;
            }

            seen.add(key);
            emails.push(value);
        });

    return emails;
};

const parseReplyToEmailsJson = (value: string | null | undefined) => {
    if (!value?.trim()) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(value);
        return Array.isArray(parsed)
            ? normalizeEmailList(parsed.filter((email): email is string => typeof email === "string"))
            : [];
    } catch {
        return [];
    }
};

const uniqueTextOptions = (values: Array<string | null | undefined>) => {
    const optionsByKey = new Map<string, {label: string; value: string}>();

    values
        .map(value => value?.trim())
        .filter((value): value is string => !!value)
        .forEach(value => {
            const key = value.toLocaleLowerCase();
            if (!optionsByKey.has(key)) {
                optionsByKey.set(key, {label: value, value});
            }
        });

    return Array.from(optionsByKey.values())
        .sort((left, right) => left.label.localeCompare(right.label));
};

const getDefaultReplyToEmail = (userName?: string) => {
    const value = userName?.trim() ?? "";

    return value.includes("@") ? value : "";
};

const formatTemplateLabel = (template: NotificationTemplate, t: (key: string, options?: Record<string, unknown>) => string) => {
    const language = template.language
        ? t(`enums:${template.language}`, {defaultValue: humanizeEnumValue(template.language)})
        : t("language", {defaultValue: "Language"});
    const subject = template.subject?.trim() || t("no-subject", {defaultValue: "No subject"});

    return `${subject} • ${language}`;
};

const formatNotificationChannel = (channel: string | null | undefined, t: (key: string, options?: Record<string, unknown>) => string) => {
    switch (channel) {
        case "EMAIL":
        case "Email":
            return t("notification-channel-email", {defaultValue: "Email"});
        case "APPLICATION":
        case "Application":
            return t("notification-channel-application", {defaultValue: "Application"});
        default:
            return channel ? humanizeEnumValue(channel) : t("notification-channel-unknown", {defaultValue: "Channel not recorded"});
    }
};

const formatParticipationFeeTypeLabel = (
    recipient: NotificationRecipient,
    t: (key: string, options?: Record<string, unknown>) => string
) => {
    const label = dedupeText([
        recipient.participationFeeTypeLabelHr,
        recipient.participationFeeTypeLabelEn
    ]).join(" / ");

    return label || t("notifications-no-participation-fee-type", {defaultValue: "No participation fee type"});
};

const recipientFullName = (recipient: NotificationRecipient) =>
    `${recipient.firstName ?? ""} ${recipient.lastName ?? ""}`
        .replace(/\s+/g, " ")
        .trim();

const recipientSearchText = (recipient: NotificationRecipient) => sortText(
    recipient.id,
    recipient.firstName,
    recipient.lastName,
    recipient.email,
    recipient.company,
    recipient.phoneNumber,
    recipient.linkedInUrl,
    recipient.additionalInfo,
    recipient.registrationId,
    recipient.registrationStatus,
    recipient.participationFeeTypeLabelHr,
    recipient.participationFeeTypeLabelEn,
    recipient.participationFeePricePeriodName
).toLocaleLowerCase();

const hasRichTextContent = (value: string) => !!stripHtmlForPreview(value).trim();

const normalizeNotificationChannelValue = (channel: string | null | undefined): NotificationChannelValue | undefined => {
    const normalized = (channel ?? "").trim().toUpperCase();
    return normalized === EventNotificationChannel.Email ? EventNotificationChannel.Email : undefined;
};

const isDraftNotificationLog = (log: NotificationHistoryLog) =>
    normalizeEnumKey(log.status) === "DRAFT";

const buildDraftGroupKey = (log: NotificationHistoryLog) => [
    log.createdAt,
    log.eventEmailTemplateId ?? "",
    log.subject ?? "",
    log.message ?? "",
    log.replyToEmailsJson ?? ""
].join("\u001f");

const hasNotificationRecipient = (log: NotificationHistoryLog) =>
    Number.isInteger(log.participantId)
    || Number.isInteger(log.registrationId)
    || !!log.recipientEmail?.trim();

const resolveDraftRecipients = (
    draftLogs: NotificationHistoryLog[],
    recipients: NotificationRecipient[]
) => {
    const recipientsByParticipantId = new Map<number, NotificationRecipient>();
    const recipientsByRegistrationId = new Map<number, NotificationRecipient>();
    const recipientsByEmail = new Map<string, NotificationRecipient>();

    recipients.forEach(recipient => {
        [recipient.id, recipient.participantId]
            .filter((id): id is number => Number.isInteger(id))
            .forEach(id => recipientsByParticipantId.set(id, recipient));

        if (Number.isInteger(recipient.registrationId)) {
            recipientsByRegistrationId.set(recipient.registrationId as number, recipient);
        }

        const emailKey = recipient.email?.trim().toLocaleLowerCase();
        if (emailKey && !recipientsByEmail.has(emailKey)) {
            recipientsByEmail.set(emailKey, recipient);
        }
    });

    const recipientIds = new Set<number>();
    const missingRecipientLabels = new Set<string>();

    draftLogs.filter(hasNotificationRecipient).forEach(log => {
        const participantId = Number.isInteger(log.participantId) ? log.participantId as number : undefined;
        const registrationId = Number.isInteger(log.registrationId) ? log.registrationId as number : undefined;
        const email = log.recipientEmail?.trim() ?? "";
        const emailKey = email.toLocaleLowerCase();
        const recipient = (participantId !== undefined
            ? recipientsByParticipantId.get(participantId)
            : undefined)
            ?? (registrationId !== undefined
                ? recipientsByRegistrationId.get(registrationId)
                : undefined)
            ?? (participantId === undefined && registrationId === undefined && emailKey
                ? recipientsByEmail.get(emailKey)
                : undefined);

        if (recipient) {
            recipientIds.add(recipient.id);
            return;
        }

        missingRecipientLabels.add(email
            || (participantId !== undefined ? `ID ${participantId}` : `ID ${registrationId}`));
    });

    return {
        recipientIds: Array.from(recipientIds),
        missingRecipientLabels: Array.from(missingRecipientLabels)
    };
};

const buildNotificationHistoryGroupKey = (log: NotificationHistoryLog) => [
    log.createdAt,
    log.eventEmailTemplateId ?? "",
    log.notificationChannel ?? "",
    log.subject ?? "",
    log.message ?? "",
    log.replyToEmailsJson ?? ""
].join("\u001f");

const buildNotificationHistoryGroups = (logs: NotificationHistoryLog[]) => {
    const groups: NotificationHistoryGroup[] = [];
    const groupsByKey = new Map<string, NotificationHistoryGroup>();

    logs.forEach(log => {
        const key = buildNotificationHistoryGroupKey(log);
        const existing = groupsByKey.get(key);
        if (existing) {
            existing.logs.push(log);
            return;
        }

        const group = {
            key,
            id: `notification-history-${log.id}`,
            representative: log,
            logs: [log]
        };
        groupsByKey.set(key, group);
        groups.push(group);
    });

    return groups;
};

const latestTimestamp = (
    logs: NotificationHistoryLog[],
    selector: (log: NotificationHistoryLog) => string | null | undefined
) => logs
    .map(selector)
    .filter((value): value is string => !!value)
    .sort((left, right) => Date.parse(right) - Date.parse(left))[0] ?? null;

const statusCountsFor = (logs: NotificationHistoryLog[]) => {
    const counts = new Map<string, number>();
    logs.forEach(log => {
        counts.set(log.status, (counts.get(log.status) ?? 0) + 1);
    });

    return Array.from(counts.entries()).map(([status, count]) => ({status, count}));
};

const getNotificationMessagePreview = (log: NotificationHistoryLog) => {
    const parsedMessage = parseEventNotificationMessage(log.message);
    return parsedMessage.body || parsedMessage.header || parsedMessage.footer;
};

const RichTextField: FC<{
    ariaLabel: string;
    label: string;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
}> = ({ariaLabel, label, onChange, placeholder, value}) => (
    <div className="notifications-rich-text-field">
        <Typography.Text strong>{label}</Typography.Text>
        <div className="notifications-rich-text-field__editor">
            <ReactQuill
                {...({"aria-label": ariaLabel} as Record<string, string>)}
                theme="snow"
                value={value}
                modules={richTextModules}
                formats={richTextFormats}
                onChange={onChange}
                placeholder={placeholder}
            />
        </div>
    </div>
);

export const NotificationsPage: FC = () => {
    const {message} = App.useApp();
    const apolloClient = useApolloClient();
    const {auth} = useContext(AuthContext);
    const {t} = useTranslation();
    const errorLabels = buildUserFacingErrorLabels(t, t("notifications-error", {
        defaultValue: "Notifications are unavailable right now."
    }));
    const {eventId: routeEventId} = useParams();
    const [page, setPage] = useState(1);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number>();
    const [selectedChannels, setSelectedChannels] = useState<NotificationChannelValue[]>([EventNotificationChannel.Email]);
    const [notificationSubject, setNotificationSubject] = useState("");
    const [notificationHeader, setNotificationHeader] = useState("");
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationFooter, setNotificationFooter] = useState("");
    const [replyToEnabled, setReplyToEnabled] = useState(false);
    const [replyToEmails, setReplyToEmails] = useState<string[]>([]);
    const [selectedRegistrationIds, setSelectedRegistrationIds] = useState<number[]>([]);
    const [recipientPickerOpen, setRecipientPickerOpen] = useState(false);
    const [recipientPickerSearch, setRecipientPickerSearch] = useState("");
    const [recipientPickerFeeTypeIds, setRecipientPickerFeeTypeIds] = useState<number[]>([]);
    const [recipientPickerRegistrationStatuses, setRecipientPickerRegistrationStatuses] = useState<string[]>([]);
    const [recipientPickerCompanies, setRecipientPickerCompanies] = useState<string[]>([]);
    const [recipientPickerSelectedIds, setRecipientPickerSelectedIds] = useState<number[]>([]);
    const [pendingDraftLoad, setPendingDraftLoad] = useState<DraftLoadState>();
    const [recipientDetailsGroupKey, setRecipientDetailsGroupKey] = useState<string>();
    const [recipientDetailsSearch, setRecipientDetailsSearch] = useState("");
    const [retryingHistoryGroupKey, setRetryingHistoryGroupKey] = useState<string>();
    const [recipientsState, setRecipientsState] = useState<{
        items: NotificationRecipient[];
        totalCount: number;
        loading: boolean;
        error?: unknown;
    }>({
        items: [],
        totalCount: 0,
        loading: false,
        error: undefined
    });

    const eventIdParam = routeEventId ?? null;
    const numericEventId = parsePositiveIntParam(eventIdParam);
    const defaultReplyToEmail = useMemo(() => getDefaultReplyToEmail(auth.userName), [auth.userName]);

        useEffect(() => {
            setPage(1);
            setSelectedTemplateId(undefined);
            setSelectedChannels([EventNotificationChannel.Email]);
            setNotificationSubject("");
            setNotificationHeader("");
            setNotificationMessage("");
            setNotificationFooter("");
            setReplyToEnabled(false);
            setReplyToEmails([]);
            setSelectedRegistrationIds([]);
            setRecipientPickerOpen(false);
            setRecipientPickerSearch("");
            setRecipientPickerFeeTypeIds([]);
            setRecipientPickerRegistrationStatuses([]);
            setRecipientPickerCompanies([]);
            setRecipientPickerSelectedIds([]);
            setPendingDraftLoad(undefined);
            setRecipientDetailsGroupKey(undefined);
            setRecipientDetailsSearch("");
            setRetryingHistoryGroupKey(undefined);
        }, [numericEventId]);

    const historyVariables = useMemo(() => numericEventId
        ? buildNotificationHistoryVariables(numericEventId, PAGE_SIZE, (page - 1) * PAGE_SIZE)
        : undefined, [numericEventId, page]);

    const listVariables = useMemo(() => numericEventId
        ? {
            eventId: numericEventId,
            take: ADMIN_MAX_PAGE_SIZE,
            skip: 0
        }
        : undefined, [numericEventId]);

    const historyQuery = useEventParticipantNotificationHistoryQuery({
        variables: historyVariables,
        skip: !historyVariables,
        fetchPolicy: "cache-and-network"
    });
    const templatesQuery = useEventNotificationTemplatesQuery({
        variables: listVariables,
        skip: !listVariables,
        fetchPolicy: "cache-and-network"
    });
    const [sendEventRegistrationNotification, sendState] = useSendEventRegistrationNotificationMutation();
    const [saveEventRegistrationNotificationDraft, saveDraftState] = useSaveEventRegistrationNotificationDraftMutation();
    const [retryFailedEventRegistrationNotifications] = useRetryFailedEventRegistrationNotificationsMutation();

    const loadRecipients = useCallback(async () => {
        if (!numericEventId) {
            setRecipientsState({
                items: [],
                totalCount: 0,
                loading: false,
                error: undefined
            });
            return;
        }

        setRecipientsState(current => ({
            ...current,
            loading: true,
            error: undefined
        }));

        try {
            const result = await apolloClient.query<EventNotificationRecipientsQuery>({
                query: EventNotificationRecipientsDocument,
                variables: {
                    eventId: numericEventId
                },
                fetchPolicy: "network-only"
            });

            const allRecipients = (result.data?.eventNotificationRecipients ?? [])
                .filter((item): item is NotificationRecipient => !!item);

            setRecipientsState({
                items: allRecipients,
                totalCount: allRecipients.length,
                loading: false,
                error: undefined
            });
        } catch (error) {
            setRecipientsState({
                items: [],
                totalCount: 0,
                loading: false,
                error
            });
        }
    }, [apolloClient, numericEventId]);

    useEffect(() => {
        void loadRecipients();
    }, [loadRecipients]);

    const logs = useMemo(() => (historyQuery.data?.eventParticipantNotificationHistory?.items ?? [])
        .filter((item): item is NotificationHistoryLog => !!item), [historyQuery.data?.eventParticipantNotificationHistory?.items]);
    const notificationHistoryGroups = useMemo(() => buildNotificationHistoryGroups(logs), [logs]);
    const recipientDetailsGroup = useMemo(
        () => notificationHistoryGroups.find(group => group.key === recipientDetailsGroupKey),
        [notificationHistoryGroups, recipientDetailsGroupKey]);
    const recipientDetailsLogs = useMemo(
        () => (recipientDetailsGroup?.logs ?? []).filter(hasNotificationRecipient),
        [recipientDetailsGroup]);
    const recipientDetailsSearchTerms = useMemo(() => recipientDetailsSearch
        .trim()
        .toLocaleLowerCase()
        .split(/\s+/)
        .filter(Boolean), [recipientDetailsSearch]);
    const filteredRecipientDetailsLogs = useMemo(() => {
        const deliveryFailureLabel = t("notification-delivery-failed-generic", {
            defaultValue: "Notification delivery failed."
        });

        return recipientDetailsLogs.filter(log => {
            const searchable = sortText(
                log.recipientEmail,
                log.notificationChannel,
                formatNotificationChannel(log.notificationChannel, t),
                log.status,
                t(`notification-status:${log.status}`, {defaultValue: humanizeEnumValue(log.status)}),
                formatNotificationTimestamp(log.createdAt),
                formatNotificationTimestamp(log.sentAt),
                formatNotificationTimestamp(log.failedAt),
                log.failureMessage
                    ? sanitizeNotificationFailureMessage(log.failureMessage, deliveryFailureLabel)
                    : t("notification-delivery-ok", {defaultValue: "No delivery error recorded."})
            ).toLocaleLowerCase();

            return recipientDetailsSearchTerms.every(term => searchable.includes(term));
        });
    }, [recipientDetailsLogs, recipientDetailsSearchTerms, t]);
    const templates = useMemo(() => (templatesQuery.data?.eventEmailTemplates?.items ?? [])
        .filter((item): item is NotificationTemplate => !!item), [templatesQuery.data?.eventEmailTemplates?.items]);
    const recipients = recipientsState.items;
    const recipientSearchTerms = useMemo(() => recipientPickerSearch
        .trim()
        .toLocaleLowerCase()
        .split(/\s+/)
        .filter(Boolean), [recipientPickerSearch]);
    const recipientFeeTypeOptions = useMemo(() => {
        const optionsById = new Map<number, {label: string; value: number}>();
        recipients.forEach(recipient => {
            const feeTypeId = Number(recipient.participationFeeTypeId);
            if (!Number.isInteger(feeTypeId) || feeTypeId <= 0 || optionsById.has(feeTypeId)) {
                return;
            }

            optionsById.set(feeTypeId, {
                value: feeTypeId,
                label: formatParticipationFeeTypeLabel(recipient, t)
            });
        });

        return Array.from(optionsById.values())
            .sort((left, right) => left.label.localeCompare(right.label));
    }, [recipients, t]);
    const recipientRegistrationStatusOptions = useMemo(() => uniqueTextOptions(recipients
        .map(recipient => recipient.registrationStatus)
        .map(status => status ? t(`enums:${status}`, {defaultValue: humanizeEnumValue(status)}) : "")), [recipients, t]);
    const recipientCompanyOptions = useMemo(() => uniqueTextOptions(recipients.map(recipient => recipient.company)), [recipients]);
    const hasActiveRecipientPickerFilters = recipientPickerSearch.trim().length > 0
        || recipientPickerFeeTypeIds.length > 0
        || recipientPickerRegistrationStatuses.length > 0
        || recipientPickerCompanies.length > 0;
    const filteredRecipients = useMemo(() => {
        return recipients.filter(recipient => {
            const searchable = recipientSearchText(recipient);
            const matchesSearch = recipientSearchTerms.length === 0 ||
                recipientSearchTerms.every(term => searchable.includes(term));
            const matchesFeeType = recipientPickerFeeTypeIds.length === 0 ||
                recipientPickerFeeTypeIds.includes(Number(recipient.participationFeeTypeId));
            const statusLabel = recipient.registrationStatus
                ? t(`enums:${recipient.registrationStatus}`, {defaultValue: humanizeEnumValue(recipient.registrationStatus)})
                : "";
            const matchesStatus = recipientPickerRegistrationStatuses.length === 0 ||
                recipientPickerRegistrationStatuses.includes(statusLabel);
            const matchesCompany = recipientPickerCompanies.length === 0 ||
                recipientPickerCompanies.includes(recipient.company?.trim() ?? "");

            return matchesSearch && matchesFeeType && matchesStatus && matchesCompany;
        });
    }, [
        recipientPickerCompanies,
        recipientPickerFeeTypeIds,
        recipientPickerRegistrationStatuses,
        recipientSearchTerms,
        recipients,
        t
    ]);
    useEffect(() => {
        if (recipientDetailsGroupKey && !recipientDetailsGroup) {
            setRecipientDetailsGroupKey(undefined);
        }
    }, [recipientDetailsGroup, recipientDetailsGroupKey]);

    useEffect(() => {
        if (selectedTemplateId && !templates.some(template => template.id === selectedTemplateId)) {
            setSelectedTemplateId(undefined);
        }
    }, [selectedTemplateId, templates]);

    const selectedRecipientCount = selectedRegistrationIds.length;
    const hasEmailChannel = selectedChannels.includes(EventNotificationChannel.Email);
    const hasNotificationContent = hasRichTextContent(notificationHeader)
        || hasRichTextContent(notificationMessage)
        || hasRichTextContent(notificationFooter);
    const normalizedReplyToEmails = useMemo(
        () => replyToEnabled ? normalizeEmailList(replyToEmails) : [],
        [replyToEmails, replyToEnabled]);
    // The first unmet requirement is shown next to the disabled Send button.
    const sendDisabledReason = !numericEventId
        ? t("notifications-send-reason-event", {defaultValue: "Open an event first."})
        : selectedRegistrationIds.length === 0
            ? t("notifications-send-reason-recipients", {defaultValue: "Select at least one recipient."})
            : selectedChannels.length === 0
                ? t("notifications-send-reason-channel", {defaultValue: "Select a channel."})
                : !hasNotificationContent
                    ? t("notifications-send-reason-content", {defaultValue: "Write the message content."})
                    : hasEmailChannel && !notificationSubject.trim()
                        ? t("notifications-send-reason-subject", {defaultValue: "Enter the email subject."})
                        : hasEmailChannel && replyToEnabled && normalizedReplyToEmails.length === 0
                            ? t("notifications-send-reason-reply-to", {defaultValue: "Enter a reply-to address or turn reply-to off."})
                            : undefined;
    const isSendDisabled = !!sendDisabledReason;

        useEffect(() => {
            if (recipientsState.loading || recipients.length === 0) {
                return;
            }

            setSelectedRegistrationIds(current => current.filter(id => recipients.some(recipient => recipient.id === id)));
        }, [recipients, recipientsState.loading]);

        const handleTemplateChange = useCallback((value: number | string | undefined | null) => {
            if (value === undefined || value === null || value === "") {
                setSelectedTemplateId(undefined);
                return;
            }

            const templateId = Number(value);
            setSelectedTemplateId(templateId);
            const template = templates.find(candidate => candidate.id === templateId);
            if (!template) {
                return;
            }

            const bodyParts = parseEventEmailTemplateBody(template.body);
            setNotificationSubject(template.subject?.trim() ?? "");
            setNotificationHeader(bodyParts.header);
            setNotificationFooter(bodyParts.footer);
        }, [templates]);

    const templateOptions = useMemo(() => templates.map(template => ({
        label: formatTemplateLabel(template, t),
        value: template.id
    })), [t, templates]);
    const replyToOptions = useMemo(() => normalizeEmailList([
        defaultReplyToEmail,
        ...replyToEmails
    ]).map(email => ({
        label: email,
        value: email
    })), [defaultReplyToEmail, replyToEmails]);

    const handleReplyToEnabledChange = useCallback((event: {target: {checked: boolean}}) => {
        const checked = event.target.checked;
        setReplyToEnabled(checked);

        if (!checked) {
            setReplyToEmails([]);
            return;
        }

        setReplyToEmails(current => current.length > 0
            ? normalizeEmailList(current)
            : normalizeEmailList([defaultReplyToEmail]));
    }, [defaultReplyToEmail]);

    const resetRecipientPickerFilters = useCallback(() => {
        setRecipientPickerSearch("");
        setRecipientPickerFeeTypeIds([]);
        setRecipientPickerRegistrationStatuses([]);
        setRecipientPickerCompanies([]);
    }, []);

    const openRecipientPicker = useCallback(() => {
        resetRecipientPickerFilters();
        setRecipientPickerSelectedIds(selectedRegistrationIds);
        setRecipientPickerOpen(true);
    }, [resetRecipientPickerFilters, selectedRegistrationIds]);

    const handleSavePickerSelection = useCallback(() => {
        const validRecipientIds = new Set(recipients.map(recipient => recipient.id));
        setSelectedRegistrationIds(Array.from(new Set(recipientPickerSelectedIds))
            .filter(id => validRecipientIds.has(id)));
        setRecipientPickerOpen(false);
    }, [recipientPickerSelectedIds, recipients]);

    const handleAddFilteredRecipients = useCallback(() => {
        setRecipientPickerSelectedIds(current => Array.from(new Set([
            ...current,
            ...filteredRecipients.map(recipient => recipient.id)
        ])));
    }, [filteredRecipients]);

    const applyDraftLoad = useCallback((draftLoad: DraftLoadState) => {
        const messageParts = parseEventNotificationMessage(draftLoad.draftLog.message);
        const restoredReplyToEmails = normalizeEmailList(draftLoad.replyToEmails);

        setSelectedTemplateId(draftLoad.draftLog.eventEmailTemplateId ?? undefined);
        setSelectedChannels(draftLoad.channels.length > 0 ? draftLoad.channels : [EventNotificationChannel.Email]);
        setNotificationSubject(draftLoad.draftLog.subject ?? "");
        setNotificationHeader(messageParts.header);
        setNotificationMessage(messageParts.body);
        setNotificationFooter(messageParts.footer);
        setReplyToEnabled(restoredReplyToEmails.length > 0);
        setReplyToEmails(restoredReplyToEmails);
        setSelectedRegistrationIds(draftLoad.recipientIds);
        setRecipientPickerSelectedIds(draftLoad.recipientIds);
        message.success(t("notifications-draft-loaded-success", {
            defaultValue: "Draft loaded into the form."
        }));
    }, [message, t]);

    const handleLoadDraft = useCallback((draftLog: NotificationHistoryLog) => {
        if (!isDraftNotificationLog(draftLog)) {
            return;
        }

        if (recipientsState.loading) {
            message.warning(t("notifications-draft-recipients-loading", {
                defaultValue: "Wait for the recipient list to finish loading."
            }));
            return;
        }

        if (recipientsState.error) {
            message.error(t("notifications-draft-recipients-unavailable", {
                defaultValue: "The draft cannot be loaded while the recipient list is unavailable."
            }));
            return;
        }

        const draftGroupKey = buildDraftGroupKey(draftLog);
        const draftGroup = logs.filter(log => isDraftNotificationLog(log) && buildDraftGroupKey(log) === draftGroupKey);
        const channels = Array.from(new Set(draftGroup
            .map(log => normalizeNotificationChannelValue(log.notificationChannel))
            .filter((value): value is NotificationChannelValue => !!value)));
        const resolvedRecipients = resolveDraftRecipients(draftGroup, recipients);
        const draftLoad = {
            draftLog,
            channels,
            replyToEmails: normalizeEmailList(draftGroup.flatMap(log =>
                parseReplyToEmailsJson(log.replyToEmailsJson))),
            ...resolvedRecipients
        };

        if (resolvedRecipients.missingRecipientLabels.length > 0) {
            setPendingDraftLoad(draftLoad);
            return;
        }

        applyDraftLoad(draftLoad);
    }, [message, applyDraftLoad, logs, recipients, recipientsState.error, recipientsState.loading, t]);

    const confirmDraftLoad = useCallback(() => {
        if (!pendingDraftLoad) {
            return;
        }

        applyDraftLoad(pendingDraftLoad);
        setPendingDraftLoad(undefined);
    }, [applyDraftLoad, pendingDraftLoad]);

    const handleRetryFailedGroup = useCallback(async (group: NotificationHistoryGroup) => {
        if (!numericEventId || retryingHistoryGroupKey) {
            return;
        }

        const notificationLogIds = group.logs
            .filter(log => normalizeEnumKey(log.status) === "FAILED"
                && normalizeNotificationChannelValue(log.notificationChannel) === EventNotificationChannel.Email)
            .map(log => log.id);
        if (notificationLogIds.length === 0) {
            return;
        }

        setRetryingHistoryGroupKey(group.key);
        try {
            const result = await retryFailedEventRegistrationNotifications({
                variables: {
                    eventId: numericEventId,
                    notificationLogIds
                }
            });
            await historyQuery.refetch();
            message.success(t("notifications-retry-queued-success", {
                defaultValue: "Retry queued for {{count}} recipients.",
                count: result.data?.retryFailedEventRegistrationNotifications ?? notificationLogIds.length
            }));
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        } finally {
            setRetryingHistoryGroupKey(undefined);
        }
    }, [
        message,
        errorLabels,
        historyQuery,
        numericEventId,
        retryFailedEventRegistrationNotifications,
        retryingHistoryGroupKey,
        t
    ]);

    const recipientPickerColumns = useMemo<TableColumnsType<NotificationRecipient>>(() => [
        {
            key: "name",
            title: t("name", {defaultValue: "Name"}),
            sorter: textSorter(recipient => sortText(recipient.firstName, recipient.lastName)),
            render: (_, recipient) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text>{recipientFullName(recipient) || "-"}</Typography.Text>
                    <Typography.Text type="secondary">
                        {recipient.registrationId
                            ? t("notifications-recipient-registration-id", {
                                defaultValue: "Registration {{id}}",
                                id: recipient.registrationId
                            })
                            : `ID ${recipient.id}`}
                    </Typography.Text>
                </Space>
            )
        },
        {
            key: "registrationStatus",
            title: t("registration-status", {defaultValue: "Registration status"}),
            sorter: textSorter(recipient => recipient.registrationStatus),
            render: (_, recipient) => recipient.registrationStatus ? (
                <StatusTag>
                    {t(`enums:${recipient.registrationStatus}`, {
                        defaultValue: humanizeEnumValue(recipient.registrationStatus)
                    })}
                </StatusTag>
            ) : "-"
        },
        {
            key: "participationFeeType",
            title: t("participation-fee-type", {defaultValue: "Participation fee type"}),
            sorter: textSorter(recipient => sortText(
                recipient.participationFeeTypeLabelHr,
                recipient.participationFeeTypeLabelEn,
                recipient.participationFeePricePeriodName
            )),
            render: (_, recipient) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text>{formatParticipationFeeTypeLabel(recipient, t)}</Typography.Text>
                    {recipient.participationFeePricePeriodName ? (
                        <Typography.Text type="secondary">{recipient.participationFeePricePeriodName}</Typography.Text>
                    ) : null}
                </Space>
            )
        },
        {
            key: "email",
            title: t("email", {defaultValue: "Email"}),
            sorter: textSorter(recipient => recipient.email),
            render: (_, recipient) => recipient.email || "-"
        },
        {
            key: "company",
            title: t("company", {defaultValue: "Company"}),
            sorter: textSorter(recipient => recipient.company),
            render: (_, recipient) => recipient.company || "-"
        },
        {
            key: "phoneNumber",
            title: t("phone", {defaultValue: "Phone"}),
            sorter: textSorter(recipient => recipient.phoneNumber),
            render: (_, recipient) => recipient.phoneNumber || "-"
        },
        {
            key: "additionalInfo",
            title: t("additional-info", {defaultValue: "Additional"}),
            sorter: textSorter(recipient => sortText(recipient.additionalInfo, recipient.linkedInUrl)),
            render: (_, recipient) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text>{recipient.additionalInfo || "-"}</Typography.Text>
                    {recipient.linkedInUrl ? (
                        <Typography.Text type="secondary">{recipient.linkedInUrl}</Typography.Text>
                    ) : null}
                </Space>
            )
        }
    ], [t]);

    const recipientDetailColumns = useMemo<TableColumnsType<NotificationHistoryLog>>(() => [
        {
            key: "recipient",
            title: t("notification-recipient", {defaultValue: "Recipient"}),
            sorter: textSorter(log => log.recipientEmail),
            render: (_, log) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text>{log.recipientEmail || "-"}</Typography.Text>
                    <StatusTag icon={log.notificationChannel === EventNotificationChannel.Email ? <MailOutlined/> : undefined} tone={log.notificationChannel === EventNotificationChannel.Email ? "info" : "info"}>
                        {formatNotificationChannel(log.notificationChannel, t)}
                    </StatusTag>
                </Space>
            )
        },
        {
            key: "status",
            title: t("status", {defaultValue: "Status"}),
            sorter: textSorter(log => log.status),
            render: (_, log) => (
                <StatusTag tone={notificationStatusTone(log.status)}>
                    {t(`notification-status:${log.status}`, {defaultValue: humanizeEnumValue(log.status)})}
                </StatusTag>
            )
        },
        {
            key: "timeline",
            title: t("delivery-timeline", {defaultValue: "Delivery timeline"}),
            sorter: dateSorter(log => log.createdAt),
            render: (_, log) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text>
                        {t("created-timeline", {
                            defaultValue: "Created: {{value}}",
                            value: formatNotificationTimestamp(log.createdAt)
                        })}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("sent-timeline", {
                            defaultValue: "Sent: {{value}}",
                            value: formatNotificationTimestamp(log.sentAt)
                        })}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("failed-timeline", {
                            defaultValue: "Failed: {{value}}",
                            value: formatNotificationTimestamp(log.failedAt)
                        })}
                    </Typography.Text>
                </Space>
            )
        },
        {
            key: "failureMessage",
            title: t("delivery-note", {defaultValue: "Delivery note"}),
            sorter: textSorter(log => log.failureMessage),
            render: (_, log) => (
                log.failureMessage
                    ? <Typography.Text type="danger">{sanitizeNotificationFailureMessage(log.failureMessage, t("notification-delivery-failed-generic", {
                        defaultValue: "Notification delivery failed."
                    }))}</Typography.Text>
                    : <Typography.Text type="secondary">{t("notification-delivery-ok", {defaultValue: "No delivery error recorded."})}</Typography.Text>
            )
        }
    ], [t]);

    const columns = useMemo<TableColumnsType<NotificationHistoryGroup>>(() => [
        {
            key: "recipient",
            title: t("notification-recipient", {defaultValue: "Recipient"}),
            sorter: textSorter(group => sortText(
                group.logs.filter(hasNotificationRecipient).length,
                ...group.logs.map(log => log.recipientEmail),
                group.representative.notificationChannel
            )),
            render: (_, group) => {
                const recipientCount = group.logs.filter(hasNotificationRecipient).length;

                return (
                    <Space direction="vertical" size={4}>
                        <Button
                            disabled={recipientCount === 0}
                            onClick={() => {
                                setRecipientDetailsSearch("");
                                setRecipientDetailsGroupKey(group.key);
                            }}
                        >
                            {t("notifications-recipients-button", {defaultValue: "Recipients"})}
                        </Button>
                        <Typography.Text type="secondary">
                            {t("notifications-recipient-count", {
                                defaultValue: "{{count}} recipients",
                                count: recipientCount
                            })}
                        </Typography.Text>
                        <StatusTag
                            icon={group.representative.notificationChannel === EventNotificationChannel.Email ? <MailOutlined/> : undefined}
                            tone={group.representative.notificationChannel === EventNotificationChannel.Email ? "info" : "info"}
                        >
                            {formatNotificationChannel(group.representative.notificationChannel, t)}
                        </StatusTag>
                    </Space>
                );
            }
        },
        {
            key: "template",
            title: t("template", {defaultValue: "Template"}),
            sorter: textSorter(group => sortText(
                group.representative.subject,
                stripHtmlForPreview(getNotificationMessagePreview(group.representative))
            )),
            render: (_, group) => {
                const log = group.representative;
                return (
                    <Space direction="vertical" size={0}>
                        <Typography.Text>
                            {log.subject || stripHtmlForPreview(getNotificationMessagePreview(log)) || t("notification-no-message-preview", {defaultValue: "No content recorded"})}
                        </Typography.Text>
                        {log.eventEmailTemplateId ? null : (
                            <Typography.Text type="secondary">
                                {t("notification-without-template", {defaultValue: "No template"})}
                            </Typography.Text>
                        )}
                    </Space>
                );
            }
        },
        {
            key: "status",
            title: t("status", {defaultValue: "Status"}),
            sorter: textSorter(group => sortText(...statusCountsFor(group.logs).map(item => `${item.status} ${item.count}`))),
            render: (_, group) => (
                <Space wrap size={4}>
                    {statusCountsFor(group.logs).map(({status, count}) => (
                        <StatusTag key={status} tone={notificationStatusTone(status)}>
                            {t(`notification-status:${status}`, {defaultValue: humanizeEnumValue(status)})}
                            {group.logs.length > 1 ? ` (${count})` : ""}
                        </StatusTag>
                    ))}
                </Space>
            )
        },
        {
            key: "timeline",
            title: t("delivery-timeline", {defaultValue: "Delivery timeline"}),
            sorter: dateSorter(group => group.representative.createdAt),
            render: (_, group) => (
                <Space direction="vertical" size={0}>
                    <Typography.Text>
                        {t("created-timeline", {
                            defaultValue: "Created: {{value}}",
                            value: formatNotificationTimestamp(group.representative.createdAt)
                        })}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("sent-timeline", {
                            defaultValue: "Sent: {{value}}",
                            value: formatNotificationTimestamp(latestTimestamp(group.logs, log => log.sentAt))
                        })}
                    </Typography.Text>
                    <Typography.Text type="secondary">
                        {t("failed-timeline", {
                            defaultValue: "Failed: {{value}}",
                            value: formatNotificationTimestamp(latestTimestamp(group.logs, log => log.failedAt))
                        })}
                    </Typography.Text>
                </Space>
            )
        },
        {
            key: "failureMessage",
            title: t("delivery-note", {defaultValue: "Delivery note"}),
            sorter: textSorter(group => sortText(...group.logs.map(log => log.failureMessage))),
            render: (_, group) => {
                const failedLogs = group.logs.filter(log => !!log.failureMessage);
                if (failedLogs.length === 0) {
                    return <Typography.Text type="secondary">{t("notification-delivery-ok", {defaultValue: "No delivery error recorded."})}</Typography.Text>;
                }

                if (failedLogs.length === 1) {
                    return (
                        <Typography.Text type="danger">{sanitizeNotificationFailureMessage(failedLogs[0].failureMessage, t("notification-delivery-failed-generic", {
                            defaultValue: "Notification delivery failed."
                        }))}</Typography.Text>
                    );
                }

                return (
                    <Typography.Text type="danger">
                        {t("notification-delivery-multiple-failures", {
                            defaultValue: "{{count}} failed deliveries. Open recipients for details.",
                            count: failedLogs.length
                        })}
                    </Typography.Text>
                );
            }
        },
        {
            key: "actions",
            title: t("actions", {defaultValue: "Actions"}),
            render: (_, group) => {
                if (isDraftNotificationLog(group.representative)) {
                    return (
                        <Button onClick={() => handleLoadDraft(group.representative)}>
                            {t("notifications-load-draft", {defaultValue: "Load draft"})}
                        </Button>
                    );
                }

                const hasFailedEmail = group.logs.some(log =>
                    normalizeEnumKey(log.status) === "FAILED"
                    && normalizeNotificationChannelValue(log.notificationChannel) === EventNotificationChannel.Email);
                if (!hasFailedEmail) {
                    return <Typography.Text type="secondary">-</Typography.Text>;
                }

                return (
                    <Button
                        loading={retryingHistoryGroupKey === group.key}
                        disabled={!!retryingHistoryGroupKey && retryingHistoryGroupKey !== group.key}
                        onClick={() => void handleRetryFailedGroup(group)}
                    >
                        {t("notifications-retry-failed", {defaultValue: "Retry failed"})}
                    </Button>
                );
            }
        }
    ], [handleLoadDraft, handleRetryFailedGroup, retryingHistoryGroupKey, t]);

        const handleSaveDraft = useCallback(async () => {
            if (!numericEventId) {
                return;
            }

            try {
                const result = await saveEventRegistrationNotificationDraft({
                    variables: {
                        input: {
                            eventId: numericEventId,
                            eventEmailTemplateId: selectedTemplateId ?? null,
                            channels: selectedChannels,
                            subject: notificationSubject.trim(),
                            message: serializeEventNotificationMessage({
                                header: notificationHeader,
                                body: notificationMessage,
                                footer: notificationFooter
                            }),
                            replyToEmails: normalizedReplyToEmails,
                            registrationIds: [],
                            participantIds: selectedRegistrationIds
                        }
                    }
                });
                setPage(1);
                await historyQuery.refetch();

                const saveSummary = result.data?.saveEventRegistrationNotificationDraft;
                message.success(t("notifications-draft-save-success-count", {
                    defaultValue: "Notification draft saved for {{count}} recipients.",
                    count: saveSummary?.requestedCount ?? selectedRegistrationIds.length
                }));
            } catch (error) {
                message.error(t("notifications-draft-save-failed", {
                    defaultValue: "Notification draft could not be saved."
                }));
            }
        }, [
            message,
            notificationFooter,
            notificationHeader,
            notificationMessage,
            notificationSubject,
            normalizedReplyToEmails,
            numericEventId,
            historyQuery,
            saveEventRegistrationNotificationDraft,
            selectedChannels,
            selectedRegistrationIds,
            selectedTemplateId,
            t
        ]);

        const handleSend = useCallback(async () => {
            if (!numericEventId || isSendDisabled) {
                return;
        }

        try {
            const result = await sendEventRegistrationNotification({
                variables: {
                    input: {
                        eventId: numericEventId,
                        eventEmailTemplateId: selectedTemplateId ?? null,
                        channels: selectedChannels,
                        subject: notificationSubject.trim(),
                        message: serializeEventNotificationMessage({
                            header: notificationHeader,
                            body: notificationMessage,
                            footer: notificationFooter
                        }),
                        replyToEmails: normalizedReplyToEmails,
                        registrationIds: [],
                        participantIds: selectedRegistrationIds
                    }
                }
            });

            const sendSummary = result.data?.sendEventRegistrationNotification;
            await historyQuery.refetch();

                if (!sendSummary) {
                    message.success(t("notifications-send-success", {
                        defaultValue: "Notifications queued."
                    }));
                    setSelectedRegistrationIds([]);
                    setNotificationMessage("");
                    return;
            }

            if (sendSummary.queuedCount > 0 && sendSummary.failedCount === 0) {
                message.success(t("notifications-send-queued-count", {
                    defaultValue: "Queued {{count}} notifications.",
                    count: sendSummary.queuedCount
                }));
            } else if (sendSummary.queuedCount > 0 && sendSummary.failedCount > 0) {
                message.warning(t("notifications-send-queued-partial-count", {
                    defaultValue: "Queued {{queuedCount}} notifications. {{failedCount}} could not be queued. Review history for details.",
                    queuedCount: sendSummary.queuedCount,
                    failedCount: sendSummary.failedCount
                }));
            } else {
                message.error(t("notifications-send-failed-count", {
                    defaultValue: "No notifications could be queued. {{failedCount}} failed. Review history for details.",
                    failedCount: sendSummary.failedCount
                }));
            }

                setSelectedRegistrationIds([]);
                setNotificationMessage("");
        } catch (error) {
            message.error(getUserFacingErrorMessage(error, t, errorLabels));
        }
    }, [
        message,
        errorLabels,
        historyQuery,
        isSendDisabled,
        notificationFooter,
        notificationHeader,
        notificationMessage,
        notificationSubject,
        normalizedReplyToEmails,
        numericEventId,
        selectedChannels,
        selectedRegistrationIds,
        selectedTemplateId,
        sendEventRegistrationNotification,
        t
    ]);

    const historyContent = (
        <div className="ati-stack">
            <div className="ati-stack ati-stack--compact">
                <Typography.Title level={5} style={{margin: 0}}>
                    {t("notifications-compose-title", {defaultValue: "Send event notification"})}
                </Typography.Title>
                <Typography.Text type="secondary">
                    {t("notifications-compose-description", {
                        defaultValue: "Choose a notification channel, optionally use a template, and send a message to selected participants."
                    })}
                </Typography.Text>

                {templatesQuery.error && (
                    <Alert
                        type="error"
                        showIcon
                        message={t("notifications-templates-error-title", {defaultValue: "Email templates are unavailable"})}
                        description={getUserFacingErrorMessage(templatesQuery.error, t, errorLabels)}
                    />
                )}

                {!!recipientsState.error && (
                    <Alert
                        type="error"
                        showIcon
                        message={t("notifications-recipients-error-title", {defaultValue: "Recipients are unavailable"})}
                        description={getUserFacingErrorMessage(recipientsState.error, t, errorLabels)}
                    />
                )}

                {!recipientsState.error && recipients.length === 0 && numericEventId && (
                    <Alert
                        type="info"
                        showIcon
                        message={t("notifications-no-recipients", {defaultValue: "No event participants are available for notifications yet."})}
                    />
                )}

                <div>
                    <Typography.Text strong>{t("notification-channel", {defaultValue: "Notification channel"})}</Typography.Text>
                    <Select
                        aria-label={t("notification-channel", {defaultValue: "Notification channel"})}
                        mode="multiple"
                        options={notificationChannelOptions.map(option => ({
                            ...option,
                            label: t(`notification-channel-option:${option.value}`, {defaultValue: option.label})
                        }))}
                        value={selectedChannels}
                        onChange={values => setSelectedChannels(values
                            .filter((value): value is NotificationChannelValue => value === EventNotificationChannel.Email))}
                        disabled={!numericEventId}
                        placeholder={t("notifications-select-channel", {defaultValue: "Select a notification channel"})}
                        style={{display: "block", marginTop: 8}}
                    />
                </div>

                <div>
                    <Typography.Text strong>{t("template", {defaultValue: "Template"})}</Typography.Text>
                    <Select
                        aria-label={t("template", {defaultValue: "Template"})}
                        allowClear
                            options={templateOptions}
                            value={selectedTemplateId}
                            onChange={handleTemplateChange}
                            disabled={!numericEventId || templates.length === 0}
                        loading={templatesQuery.loading}
                        placeholder={t("notifications-select-template", {defaultValue: "Select a template (optional)"})}
                        style={{display: "block", marginTop: 8}}
                    />
                </div>

                <div>
                    <Typography.Text strong>{t("subject", {defaultValue: "Subject"})}</Typography.Text>
                    <Input
                        aria-label={t("subject", {defaultValue: "Subject"})}
                        value={notificationSubject}
                        onChange={event => setNotificationSubject(event.target.value)}
                        placeholder={t("notifications-template-subject-placeholder", {
                            defaultValue: "Enter notification subject."
                        })}
                        style={{marginTop: 8}}
                    />
                </div>

                <div>
                    <Checkbox
                        checked={replyToEnabled}
                        disabled={!numericEventId || !hasEmailChannel}
                        onChange={handleReplyToEnabledChange}
                    >
                        {t("notifications-reply-to-enabled", {defaultValue: "Use a Reply-To address"})}
                    </Checkbox>
                    {replyToEnabled && (
                        <Select
                            aria-label={t("notifications-reply-to-addresses", {defaultValue: "Reply-To addresses"})}
                            mode="tags"
                            options={replyToOptions}
                            value={replyToEmails}
                            onChange={values => setReplyToEmails(normalizeEmailList(values.map(value => String(value))))}
                            disabled={!numericEventId || !hasEmailChannel}
                            placeholder={t("notifications-reply-to-placeholder", {
                                defaultValue: "Enter Reply-To addresses"
                            })}
                            style={{display: "block", marginTop: 8}}
                        />
                    )}
                </div>

                <RichTextField
                    ariaLabel={t("notifications-header", {defaultValue: "Header"})}
                    label={t("notifications-header", {defaultValue: "Header"})}
                    value={notificationHeader}
                    onChange={setNotificationHeader}
                    placeholder={t("notifications-header-placeholder", {
                        defaultValue: "Select a template or enter the notification header."
                    })}
                />

                <RichTextField
                    ariaLabel={t("notifications-message", {defaultValue: "Message"})}
                    label={t("notifications-message", {defaultValue: "Message"})}
                    value={notificationMessage}
                    onChange={setNotificationMessage}
                    placeholder={t("notifications-message-placeholder", {
                        defaultValue: "Enter the message sent to users."
                    })}
                />

                <RichTextField
                    ariaLabel={t("notifications-footer", {defaultValue: "Footer"})}
                    label={t("notifications-footer", {defaultValue: "Footer"})}
                    value={notificationFooter}
                    onChange={setNotificationFooter}
                    placeholder={t("notifications-footer-placeholder", {
                        defaultValue: "Select a template or enter the notification footer."
                    })}
                />

                <div>
                    <Typography.Text strong>{t("notifications-recipients", {defaultValue: "Recipients"})}</Typography.Text>
                    <Space wrap style={{display: "flex", marginTop: 8}}>
                        <Button
                            disabled={!numericEventId || recipients.length === 0}
                            loading={recipientsState.loading}
                            onClick={openRecipientPicker}
                        >
                            {t("notifications-open-recipient-picker", {defaultValue: "Select recipients"})}
                        </Button>
                        <Button
                            disabled={selectedRegistrationIds.length === 0}
                            onClick={() => setSelectedRegistrationIds([])}
                        >
                            {t("notifications-clear-recipients", {defaultValue: "Clear selection"})}
                        </Button>
                    </Space>
                </div>

                <Space wrap style={{width: "100%", justifyContent: "space-between"}}>
                    <Typography.Text type="secondary">
                        {numericEventId
                            ? t("notifications-selected-count", {
                                defaultValue: "Selected recipients: {{selected}}",
                                selected: selectedRecipientCount
                            })
                            : t("select-event-to-view-notifications", {defaultValue: "Select an event to view notification history."})}
                    </Typography.Text>
                    <Space wrap>
                            <Button
                                disabled={!numericEventId}
                                loading={saveDraftState.loading}
                                onClick={() => void handleSaveDraft()}
                            >
                                {t("notifications-save-draft", {defaultValue: "Save draft"})}
                            </Button>
                            <Button
                                type="primary"
                                icon={<SendOutlined/>}
                            disabled={isSendDisabled}
                            loading={sendState.loading}
                            onClick={() => void handleSend()}
                        >
                            {t("notifications-send", {defaultValue: "Send notification"})}
                        </Button>
                        {sendDisabledReason ? (
                            <Typography.Text type="secondary">{sendDisabledReason}</Typography.Text>
                        ) : null}
                    </Space>
                </Space>
            </div>

            <Modal
                destroyOnHidden
                open={!!pendingDraftLoad}
                title={t("notifications-draft-missing-recipients-title", {
                    defaultValue: "Some recipients are no longer available"
                })}
                okText={t("ok", {defaultValue: "OK"})}
                cancelText={t("cancel", {defaultValue: "Cancel"})}
                onOk={confirmDraftLoad}
                onCancel={() => setPendingDraftLoad(undefined)}
            >
                <Typography.Paragraph>
                    {t("notifications-draft-missing-recipients-description", {
                        defaultValue: "The following recipients from the saved draft no longer exist. Confirm to remove them from the selection."
                    })}
                </Typography.Paragraph>
                <ul className="notifications-draft-missing-recipients">
                    {pendingDraftLoad?.missingRecipientLabels.map(label => (
                        <li key={label}>
                            <Typography.Text>{label}</Typography.Text>
                        </li>
                    ))}
                </ul>
            </Modal>

            <Modal
                destroyOnHidden
                className="notifications-recipient-picker-modal"
                open={recipientPickerOpen}
                title={t("notifications-recipient-picker-title", {defaultValue: "Recipient selection"})}
                width="calc(100vw - 48px)"
                style={{top: 24}}
                onCancel={() => setRecipientPickerOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setRecipientPickerOpen(false)}>
                        {t("close", {defaultValue: "Close"})}
                    </Button>,
                    <Button
                        key="add-filtered"
                        disabled={filteredRecipients.length === 0}
                        onClick={handleAddFilteredRecipients}
                    >
                        {t("notifications-add-filtered-recipients", {defaultValue: "Add all filtered"})}
                    </Button>,
                    <Button
                        key="save-selection"
                        type="primary"
                        onClick={handleSavePickerSelection}
                    >
                        {t("notifications-save-recipient-selection", {defaultValue: "Save selection"})}
                    </Button>
                ]}
            >
                <div className="notifications-recipient-picker">
                    <aside className="notifications-recipient-picker__filters">
                        <div className="ati-stack ati-stack--compact">
                            <div>
                                <Typography.Text strong>
                                    {t("filters", {defaultValue: "Filters"})}
                                </Typography.Text>
                                <Typography.Text type="secondary" style={{display: "block", marginTop: 4}}>
                                    {t("notifications-recipient-picker-count", {
                                        defaultValue: "{{filtered}} shown, {{selected}} selected",
                                        filtered: filteredRecipients.length,
                                        selected: recipientPickerSelectedIds.length
                                    })}
                                </Typography.Text>
                            </div>
                            <Input
                                allowClear
                                aria-label={t("notifications-recipient-search", {defaultValue: "Search recipients"})}
                                value={recipientPickerSearch}
                                onChange={event => setRecipientPickerSearch(event.target.value)}
                                placeholder={t("notifications-recipient-search-placeholder", {
                                    defaultValue: "Name, email, company, phone..."
                                })}
                            />
                            <div>
                                <Typography.Text strong>
                                    {t("participation-fee-type", {defaultValue: "Participation fee type"})}
                                </Typography.Text>
                                <Select
                                    aria-label={t("participation-fee-type", {defaultValue: "Participation fee type"})}
                                    mode="multiple"
                                    allowClear
                                    options={recipientFeeTypeOptions}
                                    value={recipientPickerFeeTypeIds}
                                    onChange={values => setRecipientPickerFeeTypeIds(values
                                        .map(value => Number(value))
                                        .filter(Number.isInteger))}
                                    placeholder={t("notifications-recipient-filter-all-fee-types", {
                                        defaultValue: "All participation fee types"
                                    })}
                                    style={{display: "block", marginTop: 8}}
                                />
                            </div>
                            <div>
                                <Typography.Text strong>
                                    {t("registration-status", {defaultValue: "Registration status"})}
                                </Typography.Text>
                                <Select
                                    aria-label={t("registration-status", {defaultValue: "Registration status"})}
                                    mode="multiple"
                                    allowClear
                                    options={recipientRegistrationStatusOptions}
                                    value={recipientPickerRegistrationStatuses}
                                    onChange={values => setRecipientPickerRegistrationStatuses(values.map(value => String(value)))}
                                    placeholder={t("notifications-recipient-filter-all-statuses", {
                                        defaultValue: "All statuses"
                                    })}
                                    style={{display: "block", marginTop: 8}}
                                />
                            </div>
                            <div>
                                <Typography.Text strong>
                                    {t("company", {defaultValue: "Company"})}
                                </Typography.Text>
                                <Select
                                    aria-label={t("company", {defaultValue: "Company"})}
                                    mode="multiple"
                                    allowClear
                                    options={recipientCompanyOptions}
                                    value={recipientPickerCompanies}
                                    onChange={values => setRecipientPickerCompanies(values.map(value => String(value)))}
                                    placeholder={t("notifications-recipient-filter-all-companies", {
                                        defaultValue: "All companies"
                                    })}
                                    style={{display: "block", marginTop: 8}}
                                />
                            </div>
                            <Button
                                disabled={!hasActiveRecipientPickerFilters}
                                onClick={resetRecipientPickerFilters}
                            >
                                {t("clear-filters", {defaultValue: "Clear filters"})}
                            </Button>
                        </div>
                    </aside>
                    <section className="notifications-recipient-picker__results">
                        <Table
                            size="small"
                            rowKey="id"
                            columns={recipientPickerColumns}
                            dataSource={filteredRecipients}
                            rowSelection={{
                                preserveSelectedRowKeys: true,
                                selectedRowKeys: recipientPickerSelectedIds,
                                onChange: keys => setRecipientPickerSelectedIds(keys
                                    .map(key => Number(key))
                                    .filter(Number.isInteger))
                            }}
                            pagination={{
                                pageSize: 12,
                                hideOnSinglePage: true
                            }}
                            scroll={{x: "max-content", y: "calc(100vh - 330px)"}}
                        />
                    </section>
                </div>
            </Modal>

            {historyQuery.error && (
                <Alert
                    type="error"
                    showIcon
                    message={t("email-delivery-history-error-title", {defaultValue: "Delivery history is unavailable"})}
                    description={getUserFacingErrorMessage(historyQuery.error, t, errorLabels)}
                />
            )}

            <Space wrap style={{width: "100%"}}>
                <Typography.Text type="secondary">
                    {numericEventId
                        ? t("notification-history-count", {
                            defaultValue: "{{count}} delivery records",
                            count: historyQuery.data?.eventParticipantNotificationHistory?.totalCount ?? 0
                        })
                        : t("select-event-to-view-notifications", {defaultValue: "Select an event to view notification history."})}
                </Typography.Text>
            </Space>

            {!numericEventId && (
                <Alert
                    type="info"
                    showIcon
                    message={t("select-event-to-view-notifications", {defaultValue: "Select an event to view notification history."})}
                />
            )}

            <Table
                rowKey="id"
                columns={columns}
                dataSource={notificationHistoryGroups}
                loading={historyQuery.loading}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total: historyQuery.data?.eventParticipantNotificationHistory?.totalCount ?? 0,
                    showSizeChanger: false,
                    onChange: nextPage => setPage(nextPage)
                }}
                scroll={{x: 880}}
            />

            <Modal
                destroyOnHidden
                open={!!recipientDetailsGroup}
                title={t("notifications-recipients", {defaultValue: "Recipients"})}
                footer={null}
                width={820}
                onCancel={() => {
                    setRecipientDetailsGroupKey(undefined);
                    setRecipientDetailsSearch("");
                }}
            >
                {recipientDetailsGroup && (
                    <div className="ati-stack ati-stack--compact">
                        <Typography.Text type="secondary">
                            {recipientDetailsGroup.representative.subject
                                || stripHtmlForPreview(getNotificationMessagePreview(recipientDetailsGroup.representative))
                                || t("notification-no-message-preview", {defaultValue: "No content recorded"})}
                        </Typography.Text>
                        <Input
                            allowClear
                            aria-label={t("notifications-recipient-details-search", {defaultValue: "Search recipients"})}
                            placeholder={t("notifications-recipient-details-search-placeholder", {
                                defaultValue: "Search by email, status, or delivery"
                            })}
                            value={recipientDetailsSearch}
                            onChange={event => setRecipientDetailsSearch(event.currentTarget.value)}
                        />
                        <Typography.Text type="secondary">
                            {t("notifications-recipient-details-search-count", {
                                defaultValue: "{{shown}} of {{total}} recipients",
                                shown: filteredRecipientDetailsLogs.length,
                                total: recipientDetailsLogs.length
                            })}
                        </Typography.Text>
                        <Table
                            size="small"
                            rowKey="id"
                            columns={recipientDetailColumns}
                            dataSource={filteredRecipientDetailsLogs}
                            locale={{
                                emptyText: t("notifications-recipient-details-search-empty", {
                                    defaultValue: "No recipients match the search."
                                })
                            }}
                            pagination={{
                                pageSize: 20,
                                hideOnSinglePage: true
                            }}
                            scroll={{x: 720}}
                        />
                    </div>
                )}
            </Modal>
        </div>
    );

    return (
        <div className="ati-workspace-page">
            <PageHeader title={t("notifications", {defaultValue: "Notifications"})} level={2}/>
            {historyContent}
        </div>
    );
};

export default NotificationsPage;
